import { z } from 'zod';

/**
 * Feature-flag contracts (schema-first, ADR-0008).
 *
 * These mirror what `apps/api` actually serialises — its `domain/` types, as
 * they come off `GET /api/flags` and `GET /api/flags/:key`. They are *parsed*,
 * not cast: this is a network boundary, and a response that has drifted from the
 * API should fail loudly here rather than render as `undefined` three components
 * deep.
 *
 * ## Why these are not the shapes the mock used
 *
 * The mock invented a flattened `FeatureFlagRow` (`defaultOn`, `overrides`,
 * `totalOrgs`, `owner`) because there was no API to disagree with. The real
 * response keeps the **definition** (editorial metadata an operator authors) and
 * the **default** (the row VeevartBackend's resolver actually reads) as separate
 * objects, and that separation is load-bearing rather than incidental:
 * `lifecycle: 'deprecated'` is a label, `archived: true` is the kill switch, and
 * collapsing them into one status would let a documentation edit take a feature
 * down. The UI keeps them apart for the same reason the API does.
 *
 * The mock's `totalOrgs` has no counterpart at all — see {@link FlagListItem}.
 */

export const FlagLifecycle = z.enum(['dev', 'beta', 'ga', 'deprecated']);
export type FlagLifecycle = z.infer<typeof FlagLifecycle>;

/**
 * The flag's kind (ADR 0009 §5), mirroring the API's `domain/flag-type.ts` —
 * read that file's docblock for why the two are disjoint by consumer. Chosen
 * once, at creation, and immutable afterwards. Labels and the client-side copy
 * of the Salesforce target-field rule live in `flag-type.ts`.
 */
export const FLAG_TYPES = ['veevart2', 'salesforce'] as const;
export const FlagType = z.enum(FLAG_TYPES);
export type FlagType = z.infer<typeof FlagType>;

/**
 * The backend-environment axis (ADR 0007 Option B), mirroring the API's
 * `domain/flag-environment.ts` — read that file's docblock for why the set is
 * closed at four and why `eph` is one collective partition rather than one per
 * ephemeral stage. A plain `z.enum` over the literal array, same shape as
 * `FlagLifecycle` above and for the same reason: this is a network boundary
 * and the union has to be parsed, not assumed.
 */
export const FLAG_ENVIRONMENTS = ['dev', 'stg', 'prd', 'eph'] as const;
export const FlagEnvironment = z.enum(FLAG_ENVIRONMENTS);
export type FlagEnvironment = z.infer<typeof FlagEnvironment>;

/**
 * The editorial row that makes a flag exist — the Backoffice's replacement for
 * VeevartBackend's code-owned `FLAG_CATALOG`. None of it is read during flag
 * resolution.
 */
export const FlagDefinition = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    lifecycle: FlagLifecycle,
    owner: z.string(),
    /**
     * Always present on the wire: the API reads a row stored without one as
     * `veevart2` before it serialises it.
     */
    type: FlagType,
    /**
     * The `V2_Feature_Flags__c` field a `salesforce` flag is projected to.
     * Present on every `salesforce` flag and on no `veevart2` one.
     */
    salesforceField: z.string().optional(),
    createdAt: z.string(),
    createdBy: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    /** Optimistic-concurrency token. Required on every definition write. */
    version: z.number().int(),
});
export type FlagDefinition = z.infer<typeof FlagDefinition>;

/**
 * The platform-wide default — the row the platform resolver reads.
 *
 * `rolloutPercentage` is `.optional()` rather than nullable on purpose: an
 * absent attribute means "no rollout gate", while `0` means "off for everyone".
 * A client that coerced absent to `0` would report every ungated flag as off.
 */
export const FeatureFlagDefault = z.object({
    key: z.string(),
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().min(0).max(100).optional(),
    /** The real kill switch. Archived flags are never served, override or not. */
    archived: z.boolean(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    version: z.number().int(),
});
export type FeatureFlagDefault = z.infer<typeof FeatureFlagDefault>;

/** One museum's override. Sparse, and deliberately not versioned. */
export const TenantFlagOverride = z.object({
    tenantId: z.string(),
    key: z.string(),
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().min(0).max(100).optional(),
});
export type TenantFlagOverride = z.infer<typeof TenantFlagOverride>;

/**
 * Why a flag resolves the way it does, computed server-side by the mirrored
 * resolution rule. The UI renders this rather than a raw `enabled`, because an
 * override of `true` on an archived flag still resolves `false` — showing the
 * stored toggle there would tell an operator the opposite of the truth.
 */
export const FlagVerdictSource = z.enum(['override', 'default', 'unset', 'archived', 'rollout']);
export type FlagVerdictSource = z.infer<typeof FlagVerdictSource>;

export const FlagVerdict = z.object({
    enabled: z.boolean(),
    source: FlagVerdictSource,
});
export type FlagVerdict = z.infer<typeof FlagVerdict>;

/* ------------------------------------------------------------------ audit - */

export const FlagAuditAction = z.enum([
    'create-flag',
    'update-definition',
    'set-default',
    'set-environment-default',
    'clear-environment-default',
    'set-override',
    'clear-override',
]);
export type FlagAuditAction = z.infer<typeof FlagAuditAction>;

export const FlagActor = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    /** The AWS account the write landed in — the account *is* the environment. */
    accountId: z.string(),
});
export type FlagActor = z.infer<typeof FlagActor>;

const FlagStateSnapshot = z.object({
    enabled: z.boolean(),
    rolloutPercentage: z.number().int().optional(),
    archived: z.boolean().optional(),
});

const FlagDefinitionSnapshot = z.object({
    name: z.string(),
    description: z.string(),
    category: z.string(),
    lifecycle: FlagLifecycle,
    owner: z.string(),
    /**
     * On every definition snapshot, which is what puts the type on the
     * `create-flag` entry — the one place the decision is recorded as an event.
     */
    type: FlagType,
    salesforceField: z.string().optional(),
});

/**
 * Discriminated, so "renamed the flag" stays distinguishable from "turned it
 * off" in the history list.
 */
export const FlagAuditSnapshot = z.discriminatedUnion('type', [
    z.object({ type: z.literal('state'), state: FlagStateSnapshot }),
    z.object({ type: z.literal('definition'), definition: FlagDefinitionSnapshot }),
]);
export type FlagAuditSnapshot = z.infer<typeof FlagAuditSnapshot>;

export const FlagAuditEntry = z.object({
    key: z.string(),
    action: FlagAuditAction,
    tenantId: z.string().optional(),
    /**
     * The partition a `set-environment-default` / `clear-environment-default`
     * entry addressed. Absent for every other action, `set-default` included —
     * `set-default` always means the global row, and leaving this field off is
     * what says so on the entry itself. Mirrors
     * `FlagAuditEntry.environment` in the API's `domain/flag-audit-entry.ts`.
     */
    environment: FlagEnvironment.optional(),
    /**
     * `null` means no row existed — genuinely different from "existed and was
     * off". It is what keeps a first-time write distinguishable from a toggle
     * when someone reconstructs a timeline six months later.
     */
    before: FlagAuditSnapshot.nullable(),
    after: FlagAuditSnapshot.nullable(),
    actor: FlagActor,
    recordedAt: z.string(),
    reason: z.string().optional(),
});
export type FlagAuditEntry = z.infer<typeof FlagAuditEntry>;

/* -------------------------------------------------------------- envelopes - */

/**
 * One row of the flags list.
 *
 * There is no override count here, and that is a deliberate API decision rather
 * than an omission to paper over: counting overrides is a GSI query *per flag*,
 * so the column would cost N queries to render N rows. The detail view loads the
 * real override rows and shows a real count. The mock's "128 / 148 orgs" was a
 * number nothing in the system could have computed.
 */
export const FlagListItem = z.object({
    definition: FlagDefinition,
    default: FeatureFlagDefault,
});
export type FlagListItem = z.infer<typeof FlagListItem>;

export const FlagListResponse = z.object({ flags: z.array(FlagListItem) });

/** An overriding museum, with the verdict that override actually produces. */
export const FlagOverrideView = z.object({
    override: TenantFlagOverride,
    verdict: FlagVerdict,
});
export type FlagOverrideView = z.infer<typeof FlagOverrideView>;

/**
 * This flag's row in each backend environment's partition, present only for
 * environments that actually have one — mirrors the API's
 * `GetFlagUseCase.environmentDefaults: Partial<Record<FlagEnvironment,
 * FeatureFlag>>`.
 *
 * A `z.object` with four `.optional()` keys rather than `z.record(FlagEnvironment,
 * FeatureFlagDefault)`: zod's `ZodRecord` over a finite-literal key schema
 * infers a *fully required* `Record<FlagEnvironment, …>` on the TypeScript side
 * even though nothing at runtime requires every key to be present, which would
 * let a component read `environmentDefaults.prd.enabled` with no `undefined`
 * check and crash the moment a real flag turned out not to have a `prd` row.
 * The object form's inferred type matches the sparse reality: every key is
 * genuinely optional, both at runtime and to the type checker.
 */
export const FlagEnvironmentDefaults = z.object({
    dev: FeatureFlagDefault.optional(),
    stg: FeatureFlagDefault.optional(),
    prd: FeatureFlagDefault.optional(),
    eph: FeatureFlagDefault.optional(),
});
export type FlagEnvironmentDefaults = z.infer<typeof FlagEnvironmentDefaults>;

export const FlagDetail = z.object({
    definition: FlagDefinition,
    default: FeatureFlagDefault,
    environmentDefaults: FlagEnvironmentDefaults,
    overrides: z.array(FlagOverrideView),
    history: z.array(FlagAuditEntry),
});
export type FlagDetail = z.infer<typeof FlagDetail>;
